// src/views/InfoPage.jsx
// PUBLIC "/info" — How a Reverse Mortgage Actually Works.
// Static long-form explainer (handoff copy dated 2026-09-08). One component,
// mobile-first, no forms / popups / lead capture — phone and email only.
// No dollar figures, rates, or percentages anywhere on this page.
// Footer is the shared <PublicFooter/> (PUBLIC_DISCLOSURES in theme.js),
// identical to the direct-mail landing page footer by construction.

import React, { useEffect } from "react";
import { BRAND, PUBLIC_DISCLOSURES } from "../theme";
import { FontLoader, Wordmark, PublicFooter } from "../brand";

// Shared with the /second page so the two explainers always look alike.
const INFO_CSS=`
/* Internal scroll container — same pattern as the other public pages
   (landing/bio/opt-out), which never scroll the window itself. */
.rbr-info{height:100vh;width:100%;overflow-y:auto;background:var(--paper,#FAF8F2);color:#1A1F2E;font-family:'Mulish',system-ui,sans-serif;-webkit-font-smoothing:antialiased;}
.rbr-info .page{max-width:720px;margin:0 auto;padding:36px 22px 56px;}
.rbr-info a{color:var(--navy,#0B2A4A);}
.rbr-info .masthead{text-align:center;padding-bottom:22px;border-bottom:1px solid rgba(11,42,74,0.12);}
.rbr-info .masthead a{text-decoration:none;}
.rbr-info .lic{font-size:11px;font-weight:600;letter-spacing:0.06em;color:rgba(11,42,74,0.62);margin-top:12px;}
.rbr-info h1{font-size:34px;font-weight:900;color:var(--navy,#0B2A4A);line-height:1.15;margin:34px 0 0;letter-spacing:-0.01em;}
.rbr-info .subhead{font-size:19px;color:#4A5260;font-weight:500;line-height:1.55;margin:14px 0 0;}
.rbr-info h2{font-size:24px;font-weight:800;color:var(--navy,#0B2A4A);line-height:1.25;margin:44px 0 0;}
.rbr-info p{font-size:18px;line-height:1.7;color:#1A1F2E;margin:16px 0 0;}
.rbr-info ul{margin:16px 0 0;padding-left:24px;}
.rbr-info li{font-size:18px;line-height:1.7;color:#1A1F2E;margin-top:8px;}
.rbr-info strong{font-weight:800;color:var(--navy,#0B2A4A);}
/* Section 2 — side-by-side visual block (not a table). Stacks on mobile. */
.rbr-info .compare{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:20px;}
.rbr-info .compare .col{border-radius:14px;padding:22px 20px;border:1px solid rgba(11,42,74,0.14);background:#FFFFFF;}
.rbr-info .compare .col.reverse{background:var(--navy,#0B2A4A);border-color:var(--navy,#0B2A4A);}
.rbr-info .compare .col-tag{font-size:12px;font-weight:800;letter-spacing:0.14em;text-transform:uppercase;color:var(--clay,#C97B5C);}
.rbr-info .compare h3{font-size:19px;font-weight:800;color:var(--navy,#0B2A4A);margin:8px 0 0;line-height:1.3;}
.rbr-info .compare p{font-size:16.5px;line-height:1.65;margin-top:10px;}
.rbr-info .compare .col.reverse h3{color:#FFFFFF;}
.rbr-info .compare .col.reverse p{color:rgba(255,255,255,0.92);}
/* Use cases — three plain stacked blocks */
.rbr-info .case{border-left:3px solid var(--clay,#C97B5C);padding:2px 0 2px 18px;margin-top:22px;}
.rbr-info .case p{margin-top:6px;}
.rbr-info .case .case-lead{font-size:18px;font-weight:800;color:var(--navy,#0B2A4A);margin-top:0;}
/* Contact block */
.rbr-info .contact{background:#FFFFFF;border:1px solid rgba(11,42,74,0.14);border-radius:14px;padding:26px 24px;margin-top:26px;text-align:center;}
.rbr-info .contact .who{font-size:19px;font-weight:800;color:var(--navy,#0B2A4A);}
.rbr-info .contact .row{margin-top:12px;display:flex;flex-direction:column;gap:8px;align-items:center;}
.rbr-info .contact a{font-size:19px;font-weight:700;text-decoration:none;}
.rbr-info .contact .phone-label{font-size:14px;font-weight:700;color:#4A5260;letter-spacing:0.04em;text-transform:uppercase;}
.rbr-info .contact .site{font-size:15px;color:#4A5260;font-weight:600;}
.rbr-info footer{margin-top:52px;padding-top:26px;border-top:1px solid rgba(11,42,74,0.12);}
/* Sticky banner — back to the inquiry form, or straight to contact */
.rbr-info .guide-banner{position:sticky;top:0;z-index:20;background:var(--navy,#0B2A4A);color:#FFFFFF;display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;padding:10px 16px;box-shadow:0 2px 12px rgba(6,26,48,0.25);}
.rbr-info .guide-banner .gb-text{font-size:14.5px;font-weight:700;}
.rbr-info .guide-banner .gb-btn{font-family:inherit;font-size:14px;font-weight:800;padding:9px 16px;border-radius:4px;text-decoration:none;cursor:pointer;border:none;}
.rbr-info .guide-banner .gb-primary{background:var(--clay,#C97B5C);color:#FFFFFF;}
.rbr-info .guide-banner .gb-ghost{background:transparent;color:#FFFFFF;border:1.5px solid rgba(255,255,255,0.6);}
@media (max-width:640px){
  .rbr-info .guide-banner .gb-text{flex-basis:100%;text-align:center;font-size:13.5px;}
  .rbr-info .page{padding:26px 16px 44px;}
  .rbr-info h1{font-size:28px;margin-top:26px;}
  .rbr-info h2{font-size:22px;margin-top:38px;}
  .rbr-info .compare{grid-template-columns:1fr;}
}
`;

// Pinned banner on the guide pages: back to the "See what's available"
// inquiry for this product (opens on the homepage via ?inquire=…), or jump to
// the contact block at the bottom of the page.
function GuideBanner({product}){
  const toContact=function(){const el=document.getElementById("guide-contact");if(el)el.scrollIntoView({behavior:"smooth",block:"center"});};
  return(<div className="guide-banner">
    <span className="gb-text">{product==="hs2"?"Curious what a second could give you?":"Curious what your home could qualify for?"}</span>
    <a className="gb-btn gb-primary" href={"/?inquire="+product}>See what's available</a>
    <button className="gb-btn gb-ghost" type="button" onClick={toContact}>Contact Reece</button>
  </div>);
}

function InfoPage(){
  useEffect(()=>{
    const prev=document.title;
    document.title="How a Reverse Mortgage Actually Works — Reverse by Reece";
    return ()=>{document.title=prev;};
  },[]);
  const D=PUBLIC_DISCLOSURES.contact;

  return(<div className="rbr-info">
    <FontLoader/>
    <style>{INFO_CSS}</style>
    <GuideBanner product="ftr"/>
    <main className="page">

      {/* Masthead — wordmark links home, single license line */}
      <header className="masthead">
        <a href="/" aria-label="Reverse by Reece home"><Wordmark tone="navy" size={24}/></a>
        <div className="lic">{BRAND.loNmls} &nbsp;·&nbsp; {BRAND.companyNmls}</div>
      </header>

      <h1>How a Reverse Mortgage Actually Works</h1>
      <p className="subhead">A plain explanation from someone who does these every day. No sales pitch.</p>

      {/* ── Section 1 — Start here ── */}
      <h2>Start here</h2>
      <p>A reverse mortgage is a program that can be used a lot of different ways. It's available to homeowners 55+ in most of the states I work in, 60+ in Washington, and 62+ in Texas.</p>
      <p>First thing to clear up: <strong>you still own your home.</strong> Your name stays on title. You can sell whenever you want. The bank doesn't take the house. It's a mortgage — it's just set up differently than the one you're used to.</p>

      {/* ── Section 2 — Regular vs reverse (side-by-side visual, not a table) ── */}
      <h2>The difference between a regular mortgage and a reverse</h2>
      <div className="compare">
        <div className="col">
          <div className="col-tag">Regular mortgage</div>
          <h3>You carry the cost</h3>
          <p>You pay principal, interest, taxes and insurance every month out of your own account. Whatever's left after that is what you live on.</p>
        </div>
        <div className="col reverse">
          <div className="col-tag">Reverse mortgage</div>
          <h3>The house carries the cost</h3>
          <p>There's no required monthly mortgage payment. Instead of pulling money out of your account each month, the interest gets added to the loan balance. You're using the equity in the house to cover the cost, rather than your checking account.</p>
        </div>
      </div>
      <p>That's the whole idea. The house carries its own cost instead of you carrying it.</p>

      {/* ── Section 3 — Responsibilities ── */}
      <h2>What you're still responsible for</h2>
      <p>This part matters, so I'll say it plainly:</p>
      <ul>
        <li>You keep paying property taxes and homeowner's insurance</li>
        <li>You keep up the house</li>
        <li>You live in it as your primary home</li>
      </ul>
      <p>Do those three things and the loan stays in place as long as you want. That's the deal.</p>

      {/* ── Section 4 — Qualifying ── */}
      <h2>Qualifying works differently than any other loan</h2>
      <p>There's no minimum credit score. There's no debt-to-income ratio like a regular mortgage. Because there's no monthly payment, the income requirements are a lot more flexible than anything else out there.</p>
      <p>The program mainly runs on two things: your age, and how much equity you have.</p>
      <p>If there are issues with your credit or past payment history, it doesn't disqualify you. The loan just gets set up so a portion of your equity is set aside to cover taxes and insurance going forward. As long as there's enough equity to fund that set-aside, you can still qualify.</p>
      <p>Plenty of homeowners with clean credit choose that setup anyway. It takes two more bills off the table and keeps things simple.</p>
      <p>One thing to know if you're married: the numbers are based on the age of the youngest person on title, even if they're not on the loan.</p>

      {/* ── Section 5 — Use cases ── */}
      <h2>It's not one-size-fits-all</h2>
      <p>I've personally seen this program used three completely different ways:</p>
      <div className="case">
        <p className="case-lead">Someone in foreclosure.</p>
        <p>Behind on payments, out of options, about to lose the house. The reverse paid off the loan that was in default and stopped the process. No monthly payment going forward.</p>
      </div>
      <div className="case">
        <p className="case-lead">Someone with millions in real estate and no cash.</p>
        <p>Property paid off, worth a lot, but every dollar tied up in the walls. The reverse gave them liquidity without selling anything.</p>
      </div>
      <div className="case">
        <p className="case-lead">Someone who just wanted flexibility.</p>
        <p>They could afford a payment — they just didn't want to be locked into a required one. With a reverse, you can pay whatever you want, whenever you want, or nothing at all. It's your call, not the amortization schedule's.</p>
      </div>

      {/* ── Section 6 — The paid-off house problem ── */}
      <h2>The paid-off house problem</h2>
      <p>This is the one I see most.</p>
      <p>The house is paid off. Or close to it. But it needs a roof, or a kitchen, or a bathroom that works for the next twenty years — and there's no way to pay for it without taking on a new monthly payment. And with where rates are today, qualifying for that payment is harder than it used to be.</p>
      <p>A reverse mortgage solves that. You get the money for the improvements, there's no required monthly payment, and you're not tying up your retirement income to do it.</p>

      {/* ── Section 7 — Selling / passing away ── */}
      <h2>What happens when you sell or pass away</h2>
      <p><strong>You always keep the equity that's left.</strong> When the house sells, the loan gets paid off from the proceeds and whatever's left goes to you or your family.</p>
      <p>If the balance ever ends up higher than what the house is worth, that's not your problem or your family's — on the FHA-insured program, you never owe more than the home is worth. Your heirs can also choose to keep the house and pay off the loan if they'd rather.</p>

      {/* ── Section 8 — Closing + contact (phone and email only, no forms) ── */}
      <h2>That's the program</h2>
      <p>It's not magic and it's not for everyone. But I've heard plenty of homeowners describe it as life-saving, and it can be the difference in continuing to afford living in the property.</p>
      <p>One more thing. Before any reverse mortgage closes, you talk with an independent, HUD-approved counselor — someone who doesn't work for me or any lender. Their job is to walk through the program with you and make sure you understand it. If anything I've said here doesn't line up with what they tell you, you'll know. Everyone ends up on the same page before anything moves forward.</p>
      <p>If you want to see what it looks like on your house, call or email me. I'll tell you straight if it doesn't make sense.</p>
      <div className="contact" id="guide-contact">
        <div className="who">{D.name}</div>
        <div className="row">
          {D.phones.map(p=>(
            <a key={p.label} href={p.href}><span className="phone-label">{p.label}:</span> {p.num}</a>
          ))}
          <a href={"mailto:"+D.email}>{D.email}</a>
          <span className="site">{D.site}</span>
        </div>
      </div>

      {/* Shared compliance footer — identical to the landing page's */}
      <footer><PublicFooter/></footer>

    </main>
  </div>);
}

export { InfoPage, INFO_CSS, GuideBanner };
